import Address from '~/types/Address';
import APIAddress from '~/types/APIAddress';
import { CreateElementOptions, StripeEvent, StripeToken } from '~/types/StripeClient';
import { ActionKeys } from './constants';
import { TrackingReference } from '~/utils/TrackingHelper/types';

function loadStripe(stripePublishableKey: string) {
  return <const>{
    stripePublishableKey,
    type: ActionKeys.STRIPE_LOAD,
  };
}

function loadStripeSuccess() {
  return <const>{
    type: ActionKeys.STRIPE_LOAD_SUCCESS,
  };
}

function loadStripeError(errorMessage: string) {
  return <const>{
    errorMessage,
    type: ActionKeys.STRIPE_LOAD_ERROR,
  };
}

function createElement(
  numberElementId: string,
  expElementId: string,
  cvcElementId: string,
  cardNumberOptions: CreateElementOptions,
  cardExpOptions: CreateElementOptions,
  cardCvcOptions: CreateElementOptions,
) {
  return <const>{
    type: ActionKeys.STRIPE_CREATE_ELEMENT,
    payload: {
      numberElementId,
      expElementId,
      cvcElementId,
      cardNumberOptions,
      cardExpOptions,
      cardCvcOptions,
    },
  };
}

function createElementSuccess(elementId: string) {
  return <const>{
    elementId,
    type: ActionKeys.STRIPE_CREATE_ELEMENT_SUCCESS,
  };
}

function createElementError(elementId: string, errorMessage: string) {
  return <const>{
    elementId,
    errorMessage,
    type: ActionKeys.STRIPE_CREATE_ELEMENT_ERROR,
  };
}

function onStripeElementChange(elementId: string, event: StripeEvent) {
  return <const>{
    elementId,
    type: ActionKeys.STRIPE_ELEMENT_ON_CHANGE,
    payload: {
      event,
    },
  };
}

function createStripeToken({
  elementId,
  billingAddress,
  updatePrimaryAddress,
  trackingReference,
  creditDebitSelectionValue,
  analyticsContext,
}: {
  elementId: string;
  billingAddress: Address | APIAddress;
  updatePrimaryAddress: boolean;
  trackingReference: TrackingReference;
  creditDebitSelectionValue: 'credit' | 'debit' | 'default';
  analyticsContext?: string;
}) {
  return <const>{
    type: ActionKeys.STRIPE_CREATE_TOKEN,
    payload: {
      analyticsContext,
      elementId,
      billingAddress,
      updatePrimaryAddress,
      trackingReference,
      creditDebitSelectionValue,
    },
  };
}

function createStripeTokenSuccess(elementId: string, token: StripeToken) {
  return <const>{
    elementId,
    type: ActionKeys.STRIPE_CREATE_TOKEN_SUCCESS,
    payload: {
      token,
    },
  };
}

function createStripeTokenError(elementId: string, errorMessage: string) {
  return <const>{
    elementId,
    errorMessage,
    type: ActionKeys.STRIPE_CREATE_TOKEN_ERROR,
  };
}

function setStripeFormError(elementId: string, errorMessage: string) {
  return <const>{
    elementId,
    type: ActionKeys.STRIPE_SET_FORM_ERROR,
    payload: {
      errorMessage,
    },
  };
}

function clearStripeFormError(elementId: string) {
  return <const>{
    elementId,
    type: ActionKeys.STRIPE_CLEAR_FORM_ERROR,
  };
}

function cancelStripeFromSubmission(elementId: string) {
  return <const>{
    elementId,
    type: ActionKeys.STRIPE_CANCEL_FORM_SUBMISSION,
  };
}

function setStripeElementEmpty(elementId: string, isEmpty: boolean) {
  return <const>{
    elementId,
    isEmpty,
    type: ActionKeys.STRIPE_SET_ELEMENT_EMPTY,
  };
}

export {
  loadStripe,
  loadStripeSuccess,
  loadStripeError,
  createElement,
  createElementSuccess,
  createElementError,
  onStripeElementChange,
  createStripeToken,
  createStripeTokenSuccess,
  createStripeTokenError,
  setStripeFormError,
  clearStripeFormError,
  cancelStripeFromSubmission,
  setStripeElementEmpty,
};

export type StripeActions =
  | ReturnType<typeof loadStripe>
  | ReturnType<typeof loadStripeSuccess>
  | ReturnType<typeof loadStripeError>
  | ReturnType<typeof createElement>
  | ReturnType<typeof createElementSuccess>
  | ReturnType<typeof createElementError>
  | ReturnType<typeof onStripeElementChange>
  | ReturnType<typeof createStripeToken>
  | ReturnType<typeof createStripeTokenSuccess>
  | ReturnType<typeof createStripeTokenError>
  | ReturnType<typeof setStripeFormError>
  | ReturnType<typeof clearStripeFormError>
  | ReturnType<typeof cancelStripeFromSubmission>
  | ReturnType<typeof setStripeElementEmpty>;
